import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useForm, Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { queryClient } from 'api';
import { API_HANDLERS } from 'api/apiHandlers';
import { alert } from 'components';
import { COMPANY_NAME_IS_USED } from 'constants/errorCodes';
import { QUERIES } from 'constants/index';
import { API_ERRORS_BY_CODES } from 'constants/toasterMessages';
import { useModal } from 'hooks';
import { ApiErrors, Company, CustomField, EditCompanyFormValues } from 'types';
import { objectsDifferences } from 'utils';

import { defaultValuesAdapter, formValuesAdapter } from '../adapters';

interface UseEditCompanyFormProps {
  onClose: () => void;
  company: Company;
}
interface UseEditCompanyFormReturn {
  isLoading: boolean;
  isDirty: boolean;
  handleFormSubmit: () => void;
  control: Control<EditCompanyFormValues>;
  isOpenConfirmModal: boolean;
  handleTitleClick: () => void;
  handleCloseForm: () => void;
  handleCloseConfirmModal: () => void;
  addGeneralField: () => void;
  addContactField: () => void;
  newGeneralFields: CustomField[];
  newContactFields: CustomField[];
  setNewGeneralFields: (fields: CustomField[]) => void;
  setNewContactFields: (fields: CustomField[]) => void;
  removeGeneralField: (fieldId: string, isNewField?: boolean, index?: number) => void;
  removeContactField: (fieldId: string, isNewField?: boolean, index?: number) => void;
  isGeneralAddDisabled: boolean;
  isContactAddDisabled: boolean;
}

export function useEditCompanyForm({
  onClose,
  company,
}: UseEditCompanyFormProps): UseEditCompanyFormReturn {
  const { t } = useTranslation();
  const [newGeneralFields, setNewGeneralFields] = useState<CustomField[]>([]);
  const [newContactFields, setNewContactFields] = useState<CustomField[]>([]);
  const defaultValues: EditCompanyFormValues = defaultValuesAdapter(company);

  const {
    control,
    handleSubmit,
    setError,
    formState: { isDirty, isSubmitting },
    setValue,
  } = useForm<EditCompanyFormValues>({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const { mutateAsync: editCompanyMutation, isLoading } = useMutation(API_HANDLERS.COMPANY.EDIT, {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERIES.COMPANY_DETAILS]);
    },
    onError: (error) => {
      const errorsItem = error as AxiosError<ApiErrors>;

      if (errorsItem.response?.data.errors) {
        errorsItem.response?.data.errors.forEach((error) => {
          const errorText = API_ERRORS_BY_CODES[error.code];

          if (error.code === COMPANY_NAME_IS_USED) {
            setError('name', { message: t('company.edit.nameIsUsedError') });
          }

          alert.error(errorText);
        });
      }
    },
  });

  const {
    isOpen: isOpenConfirmModal,
    handleOpenModal: handleOpenConfirmModal,
    handleCloseModal: handleCloseConfirmModal,
  } = useModal();

  const handleFormSubmit = handleSubmit(async (data) => {
    const editInput = objectsDifferences(
      { ...defaultValues, country_code: String(company.country_code) },
      {
        ...data,
        country_code: data.country_code as string,
      },
    );

    const defaultLabels = [
      'name',
      'industry',
      'vat',
      'company_number',
      'address_1',
      'address_2',
      'city',
      'region',
      'postcode',
      'country',
      'email',
      'support_email',
      'country_code',
      'phone',
      'website',
    ];

    const filteredGeneralCustomFields =
      data?.custom_fields?.general.filter(
        (field) => field.field !== undefined && field.order !== undefined,
      ) || [];

    const filteredContactCustomFields = data?.custom_fields?.contact.filter(
      (field) => field.field !== undefined && field.order !== undefined,
    );

    // Reassign order starting from 1
    const normalizedGeneralFields = [
      ...(filteredGeneralCustomFields || []),
      ...newGeneralFields,
    ].map((field: CustomField, index: number) => ({
      ...field,
      order: index + 1,
    }));

    const normalizedContactFields = [
      ...(filteredContactCustomFields || []),
      ...newContactFields,
    ].map((field: CustomField, index: number) => ({
      ...field,
      order: index + 1,
    }));

    const generalCustomLabels =
      normalizedGeneralFields.map((field) => field.field.toLowerCase()) || [];

    const contactCustomLabels =
      normalizedContactFields.map((field) => field.field.toLowerCase()) || [];

    const allCompanyLabels = [...defaultLabels, ...generalCustomLabels, ...contactCustomLabels];
    const hasDuplicates = new Set(allCompanyLabels).size !== allCompanyLabels.length;

    if (hasDuplicates) {
      alert.error(t('company.edit.duplicateField'));
      return;
    }

    const custom_fields = {
      general: normalizedGeneralFields,
      contact: normalizedContactFields,
    };

    const formValues = {
      ...formValuesAdapter(editInput),
      custom_fields: custom_fields,
    };

    if (
      Object.keys(editInput).length === 0 &&
      !data?.custom_fields?.general?.length &&
      !data?.custom_fields?.contact?.length
    ) {
      handleCloseForm();
      return;
    }

    await editCompanyMutation(formValues);
    handleCloseForm();
  });

  const handleTitleClick = () => {
    if (isDirty || newGeneralFields.length > 0 || newContactFields.length > 0) {
      handleOpenConfirmModal();
      return;
    }
    onClose();
  };

  const handleCloseForm = () => {
    onClose();
    handleCloseConfirmModal();
  };

  const addGeneralField = () => {
    const customGeneralFields = [
      ...(defaultValues?.custom_fields?.general || []),
      ...newGeneralFields,
    ];

    // Find the maximum order value among existing fields
    // For now we are not using the order anywhere, since reordering is not a requirement atm
    const maxOrder = customGeneralFields.length
      ? Math.max(...customGeneralFields.map((field) => field.order))
      : 0;

    setNewGeneralFields([
      ...newGeneralFields,
      {
        field: '',
        value: '',
        order: maxOrder + 1,
      },
    ]);
  };

  const addContactField = () => {
    const customContactFields = [
      ...(defaultValues?.custom_fields?.contact || []),
      ...newContactFields,
    ];

    // Find the maximum order value among existing fields
    // For now we are not using the order anywhere, since reordering is not a requirement atm
    const maxOrder = customContactFields.length
      ? Math.max(...customContactFields.map((field) => field.order))
      : 0;

    setNewContactFields([...newContactFields, { field: '', value: '', order: maxOrder + 1 }]);
  };

  const removeGeneralField = (fieldId: string, isNewField = false, index?: number) => {
    if (isNewField && typeof index === 'number') {
      // Remove from newGeneralFields
      setNewGeneralFields(newGeneralFields.filter((_, i) => i !== index));
    } else {
      // Remove from existing custom_fields.general
      const updatedGeneral = (control._formValues.custom_fields?.general || []).filter(
        (field: CustomField) => field.field !== fieldId,
      );

      setValue('custom_fields.general', updatedGeneral, { shouldDirty: true });
    }
  };

  const removeContactField = (fieldId: string, isNewField = false, index?: number) => {
    if (isNewField && typeof index === 'number') {
      // Remove from newContactFields
      setNewContactFields(newContactFields.filter((_, i) => i !== index));
    } else {
      // Remove from existing custom_fields.contact
      const updatedContact = (control._formValues.custom_fields?.contact || []).filter(
        (field: CustomField) => field.field !== fieldId,
      );

      setValue('custom_fields.contact', updatedContact, { shouldDirty: true });
    }
  };

  // Disable "Add Field" if any new field has an empty key
  const isGeneralAddDisabled = newGeneralFields.some((customField) => !customField.field.trim());
  const isContactAddDisabled = newContactFields.some((customField) => !customField.field.trim());

  return {
    control,
    handleFormSubmit,
    isLoading: isLoading || isSubmitting,
    isDirty,
    isOpenConfirmModal,
    handleTitleClick,
    handleCloseForm,
    handleCloseConfirmModal,
    addGeneralField,
    addContactField,
    newGeneralFields,
    newContactFields,
    setNewGeneralFields,
    setNewContactFields,
    removeGeneralField,
    removeContactField,
    isGeneralAddDisabled,
    isContactAddDisabled,
  };
}
